import { useCallback, useEffect, useMemo, useState } from 'react';

import { IPlusDataQuery, PlusDataDocument, PosDataServiceMode } from 'generated/graphql-gateway';
import { IPluDataQuery } from 'generated/rbi-graphql';
import { IPosDataQuery, PosDataDocument } from 'generated/sanity-graphql';
import { apolloClient } from 'state/graphql/client';
import { simpleCacheQuery } from 'state/graphql/with-simple-cache';
import { LaunchDarklyFlag } from 'state/launchdarkly';
import { staticLDFlags } from 'state/launchdarkly/launchdarkly-core';
import { useServiceModeContext } from 'state/service-mode';
import { transformPlus } from 'utils/menu';
import { useSanityGqlEndpoint } from 'utils/network';
import { parseStringifiedJSON } from 'utils/parse-string';

import getRestaurantPosDataPlus from './get-restaurant-pos-data-plus';

type PosDataResults = {
  posData: Record<string, number> | null;
};

type PosDataIds = (ids?: {
  restaurantPosDataId: string | null;
  storeNumber: string | null;
  isDelivery?: boolean;
}) => Promise<PosDataResults | null>;

interface IUsePosData {
  data: PosDataResults;
  refetch: PosDataIds;
}

const transformResponse = ({
  data,
  isDelivery,
}: {
  data: IPluDataQuery & IPosDataQuery;
  isDelivery: boolean;
}): PosDataResults => {
  let rawPlus = null;

  rawPlus = getRestaurantPosDataPlus(data, isDelivery);

  if (!rawPlus) {
    return { posData: null };
  }

  return {
    posData: transformPlus(parseStringifiedJSON({ defaultValue: {}, value: rawPlus })),
  };
};

// create PLU object to keep same logic
const transformToPluObject = (plus: IPlusDataQuery['plus']): PosDataResults => {
  return {
    posData: (plus || []).reduce((acc, plu) => {
      // We aliased the properties to reduce size.
      // We were hitting a hard limit on MenuService lambdas.
      acc[plu.k] = Number(plu.v);
      return acc;
    }, {}),
  };
};

export const usePosDataQuery = ({
  restaurantPosDataId,
  storeNumber,
  lazy = false,
}: {
  restaurantPosDataId: string | null;
  storeNumber: string | null;
  lazy?: boolean;
}): IUsePosData => {
  const { isDelivery } = useServiceModeContext();
  const sanityUri = useSanityGqlEndpoint();

  const [data, setData] = useState<PosDataResults>({
    posData: null,
  });

  const fetchPosData: PosDataIds = useCallback(
    async ids => {
      const id = ids?.restaurantPosDataId ?? restaurantPosDataId;
      const storeId = ids?.storeNumber ?? storeNumber;

      if (!id) {
        return Promise.resolve({ posData: null });
      }

      let transformedResponse: PosDataResults = { posData: null };

      const enableMenuServicePlus =
        staticLDFlags.current[LaunchDarklyFlag.ENABLE_MENU_SERVICE_PLUS];

      if (enableMenuServicePlus) {
        const data = await simpleCacheQuery(apolloClient, {
          query: PlusDataDocument,
          variables: {
            storeId,
            serviceMode: ids?.isDelivery ? PosDataServiceMode.DELIVERY : PosDataServiceMode.PICKUP,
          },
        });
        transformedResponse = transformToPluObject(data?.plus);
      } else {
        const queryResponse = await apolloClient.query({
          context: { uri: sanityUri },
          query: PosDataDocument,
          variables: {
            id,
          },
        });

        transformedResponse = transformResponse({
          data: queryResponse.data,
          isDelivery: Boolean(ids?.isDelivery),
        });
      }

      setData(transformedResponse);
      return transformedResponse;
    },
    [restaurantPosDataId, sanityUri, storeNumber]
  );

  useEffect(() => {
    if (!lazy) {
      fetchPosData({
        restaurantPosDataId,
        storeNumber,
        isDelivery,
      });
    }
  }, [fetchPosData, lazy, restaurantPosDataId, storeNumber, isDelivery]);

  return useMemo(
    () => ({
      data,
      refetch: fetchPosData,
    }),
    [data, fetchPosData]
  );
};
